import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowRight, Plus, Search, Edit2, Trash2, Phone, CheckCircle, Clock, Hash, Calendar, FileText, Wrench, Printer, History, X } from 'lucide-react';
import { getSupportTickets, createSupportTicket, updateSupportTicket, deleteSupportTicket, getCustomers } from '../api';
import { matchesCustomer, customerFullName } from '../lib/customerSearch';
import { toast } from '../hooks/use-toast';

const SupportTickets = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCustomer, setFilterCustomer] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  const [timelineCustomer, setTimelineCustomer] = useState(null);

  // Form state
  const [form, setForm] = useState({
    customerName: '',
    date: new Date().toISOString().split('T')[0],
    issueDescription: '',
    solution: '',
    status: 'open'
  });
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [ticketsData, customersData] = await Promise.all([
        getSupportTickets(),
        getCustomers()
      ]);
      setTickets(ticketsData);
      setCustomers(customersData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCustomerInput = (value) => {
    setForm({ ...form, customerName: value });
    if (value.trim().length > 0) {
      const q = value.trim().toLowerCase();
      const matches = customers
        .filter(c => matchesCustomer(c, q))
        .map(c => customerFullName(c))
        .slice(0, 8);
      setCustomerSuggestions(matches);
      setShowSuggestions(matches.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };

  const selectCustomer = (name) => {
    setForm({ ...form, customerName: name });
    setShowSuggestions(false);
  };

  const handleOpenAdd = () => {
    setEditingTicket(null);
    setForm({
      customerName: '',
      date: new Date().toISOString().split('T')[0],
      issueDescription: '',
      solution: '',
      status: 'open'
    });
    setShowDialog(true);
  };

  const handleOpenEdit = (ticket) => {
    setEditingTicket(ticket);
    setForm({
      customerName: ticket.customerName,
      date: ticket.date || '',
      issueDescription: ticket.issueDescription || '',
      solution: ticket.solution || '',
      status: ticket.status || 'open'
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.customerName.trim()) {
      toast({ title: 'שגיאה', description: 'שם לקוח נדרש', variant: 'destructive' });
      return;
    }
    try {
      if (editingTicket) {
        await updateSupportTicket(editingTicket.id, form);
        toast({ title: 'עודכן', description: 'הקריאה עודכנה בהצלחה' });
      } else {
        await createSupportTicket(form);
        toast({ title: 'נוצר', description: 'קריאת שירות חדשה נוצרה' });
      }
      setShowDialog(false);
      loadData();
    } catch (error) {
      toast({ title: 'שגיאה', description: 'לא ניתן לשמור', variant: 'destructive' });
    }
  };

  const handleToggleStatus = async (ticket) => {
    const newStatus = ticket.status === 'closed' ? 'open' : 'closed';
    try {
      await updateSupportTicket(ticket.id, { status: newStatus });
      setTickets(prev => prev.map(t => t.id === ticket.id ? { ...t, status: newStatus } : t));
    } catch (error) {
      toast({ title: 'שגיאה', description: 'לא ניתן לעדכן סטטוס', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('למחוק קריאה זו?')) {
      try {
        await deleteSupportTicket(id);
        toast({ title: 'נמחק', description: 'הקריאה נמחקה' });
        loadData();
      } catch (error) {
        toast({ title: 'שגיאה', description: 'לא ניתן למחוק', variant: 'destructive' });
      }
    }
  };

  const filtered = tickets.filter(t => {
    const matchSearch = !searchQuery.trim() ||
      t.customerName.toLowerCase().includes(searchQuery.trim().toLowerCase()) ||
      (t.issueDescription || '').toLowerCase().includes(searchQuery.trim().toLowerCase()) ||
      String(t.callNumber).includes(searchQuery.trim());
    const matchStatus = filterStatus === 'all' ||
      (filterStatus === 'open' && t.status !== 'closed') ||
      (filterStatus === 'closed' && t.status === 'closed');
    const matchCustomer = filterCustomer === 'all' || t.customerName === filterCustomer;
    return matchSearch && matchStatus && matchCustomer;
  });

  const uniqueCustomers = [...new Set(tickets.map(t => t.customerName))].sort();
  const openCount = tickets.filter(t => t.status !== 'closed').length;
  const closedCount = tickets.filter(t => t.status === 'closed').length;

  const handlePrintTicket = (ticket) => {
    const printWindow = window.open('', '_blank');
    const dateStr = ticket.date ? new Date(ticket.date + 'T00:00:00').toLocaleDateString('he-IL') : '-';
    printWindow.document.write(`
      <!DOCTYPE html>
      <html dir="rtl" lang="he">
      <head>
        <meta charset="UTF-8">
        <title>קריאת שירות #${ticket.callNumber} - ${ticket.customerName}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 40px; direction: rtl; color: #1a1a1a; }
          .header { text-align: center; border-bottom: 3px solid #2563eb; padding-bottom: 20px; margin-bottom: 30px; }
          .header h1 { font-size: 22px; color: #1e3a5f; margin-bottom: 4px; }
          .header p { font-size: 13px; color: #666; }
          .company { font-size: 18px; font-weight: bold; color: #2563eb; margin-bottom: 8px; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 24px; }
          .info-box { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; }
          .info-box label { font-size: 12px; color: #64748b; font-weight: bold; display: block; margin-bottom: 4px; }
          .info-box span { font-size: 16px; font-weight: 600; }
          .section { margin-bottom: 24px; }
          .section h3 { font-size: 15px; font-weight: bold; color: #374151; margin-bottom: 8px; padding-bottom: 4px; border-bottom: 1px solid #e5e7eb; }
          .section p { font-size: 14px; line-height: 1.7; padding: 12px; background: #f9fafb; border-radius: 6px; border: 1px solid #e5e7eb; }
          .solution-box { background: #f0fdf4 !important; border-color: #bbf7d0 !important; }
          .status { display: inline-block; padding: 4px 16px; border-radius: 20px; font-size: 13px; font-weight: bold; }
          .status-open { background: #fff7ed; color: #c2410c; border: 1px solid #fdba74; }
          .status-closed { background: #f0fdf4; color: #15803d; border: 1px solid #86efac; }
          .footer { text-align: center; margin-top: 40px; padding-top: 16px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #9ca3af; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company">אימן שירותי מחשוב</div>
          <h1>קריאת שירות #${ticket.callNumber}</h1>
          <p>aiman@aimanpc.com | 052-576-7555</p>
        </div>
        <div class="info-grid">
          <div class="info-box">
            <label>שם לקוח</label>
            <span>${ticket.customerName}</span>
          </div>
          <div class="info-box">
            <label>מספר קריאה</label>
            <span>#${ticket.callNumber}</span>
          </div>
          <div class="info-box">
            <label>תאריך</label>
            <span>${dateStr}</span>
          </div>
          <div class="info-box">
            <label>סטטוס</label>
            <span class="status ${ticket.status === 'closed' ? 'status-closed' : 'status-open'}">${ticket.status === 'closed' ? 'נסגרה' : 'בטיפול'}</span>
          </div>
        </div>
        <div class="section">
          <h3>תיאור תקלה</h3>
          <p>${ticket.issueDescription || 'לא צוין'}</p>
        </div>
        <div class="section">
          <h3>פתרון</h3>
          <p class="solution-box">${ticket.solution || 'טרם הוזן פתרון'}</p>
        </div>
        <div class="footer">הופק ע"י מערכת אימן שירותי מחשוב | ${new Date().toLocaleDateString('he-IL')}</div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 300);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center" dir="rtl">
        <div className="text-xl text-gray-600">טוען קריאות שירות...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-8 px-4" dir="rtl">
      <div className="container mx-auto max-w-5xl">
        {/* Header */}
        <Card className="shadow-2xl border-0 mb-6">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-lg">
            <div className="flex justify-between items-center">
              <CardTitle className="text-3xl font-bold flex items-center gap-3">
                <Phone className="w-8 h-8" />
                קריאות שירות
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  onClick={handleOpenAdd}
                  className="bg-white text-blue-700 hover:bg-gray-100"
                  data-testid="add-ticket-btn"
                >
                  <Plus className="ml-2 w-4 h-4" />
                  קריאה חדשה
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            {/* Stats */}
            <div className="flex gap-3 mb-4">
              <div className="flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-lg border border-blue-200">
                <Phone className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-semibold text-blue-800">סה"כ: {tickets.length}</span>
              </div>
              <div className="flex items-center gap-2 bg-orange-50 px-4 py-2 rounded-lg border border-orange-200">
                <Clock className="w-4 h-4 text-orange-600" />
                <span className="text-sm font-semibold text-orange-800">בטיפול: {openCount}</span>
              </div>
              <div className="flex items-center gap-2 bg-green-50 px-4 py-2 rounded-lg border border-green-200">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-sm font-semibold text-green-800">נסגרו: {closedCount}</span>
              </div>
            </div>
            {/* Search & Filter */}
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="חיפוש לפי שם, מספר קריאה או תיאור..."
                  className="w-full pr-10 pl-4 py-2.5 border-2 border-gray-200 rounded-lg focus:border-blue-400 focus:outline-none text-sm bg-white"
                  data-testid="ticket-search"
                />
              </div>
              <select
                value={filterCustomer}
                onChange={(e) => setFilterCustomer(e.target.value)}
                className="px-4 py-2.5 border-2 border-gray-200 rounded-lg bg-white text-sm font-semibold min-w-[140px]"
                data-testid="ticket-customer-filter"
              >
                <option value="all">כל הלקוחות</option>
                {uniqueCustomers.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2.5 border-2 border-gray-200 rounded-lg bg-white text-sm font-semibold"
                data-testid="ticket-filter"
              >
                <option value="all">הכל</option>
                <option value="open">בטיפול</option>
                <option value="closed">נסגרו</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Tickets List */}
        {filtered.length === 0 ? (
          <Card className="shadow-lg">
            <CardContent className="py-16 text-center">
              <Phone className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-xl text-gray-500">{searchQuery || filterStatus !== 'all' ? 'לא נמצאו קריאות' : 'אין קריאות שירות עדיין'}</p>
              {!searchQuery && filterStatus === 'all' && (
                <Button onClick={handleOpenAdd} className="mt-4 bg-blue-600 hover:bg-blue-700 text-white">
                  צור קריאה ראשונה
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filtered.map((ticket) => (
              <div
                key={ticket.id}
                className={`bg-white rounded-xl shadow-md hover:shadow-lg transition-all border-r-4 ${ticket.status === 'closed' ? 'border-r-green-500' : 'border-r-orange-400'}`}
                data-testid={`ticket-${ticket.id}`}
              >
                <div className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Status checkbox */}
                    <button
                      onClick={() => handleToggleStatus(ticket)}
                      className={`mt-1 flex-shrink-0 w-7 h-7 rounded border-2 flex items-center justify-center transition-all ${
                        ticket.status === 'closed'
                          ? 'bg-green-500 border-green-500 text-white'
                          : 'border-gray-300 hover:border-orange-400 text-transparent hover:text-orange-300'
                      }`}
                      title={ticket.status === 'closed' ? 'נסגרה - לחץ לפתוח מחדש' : 'בטיפול - לחץ לסגור'}
                      data-testid={`toggle-status-${ticket.id}`}
                    >
                      <CheckCircle className="w-5 h-5" />
                    </button>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <button
                          onClick={() => setTimelineCustomer(ticket.customerName)}
                          className="text-lg font-bold text-blue-700 hover:text-blue-900 hover:underline transition-colors cursor-pointer"
                          title="לחץ לצפייה בהיסטוריית קריאות"
                          data-testid={`customer-timeline-${ticket.id}`}
                        >
                          {ticket.customerName}
                        </button>
                        <span className="text-sm bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                          <Hash className="w-3 h-3" />
                          {ticket.callNumber}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                          ticket.status === 'closed'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-orange-100 text-orange-700'
                        }`}>
                          {ticket.status === 'closed' ? 'נסגרה' : 'בטיפול'}
                        </span>
                      </div>

                      {/* Issue description */}
                      <div className="mb-2">
                        <p className={`text-sm text-gray-700 ${ticket.status === 'closed' ? 'line-through text-gray-400' : ''}`}>
                          <span className="font-semibold text-gray-900">תקלה: </span>
                          {ticket.issueDescription || '-'}
                        </p>
                      </div>

                      {/* Solution */}
                      {ticket.solution && (
                        <div className="bg-green-50 rounded-lg p-2.5 border border-green-200">
                          <p className="text-sm text-green-800">
                            <span className="font-semibold">פתרון: </span>
                            {ticket.solution}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Date + Actions */}
                    <div className="flex flex-col items-end gap-2 flex-shrink-0">
                      <span className="text-xs text-gray-500 flex items-center gap-1 bg-gray-100 px-2.5 py-1 rounded-lg">
                        <Calendar className="w-3 h-3" />
                        {ticket.date ? new Date(ticket.date + 'T00:00:00').toLocaleDateString('he-IL') : '-'}
                      </span>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handlePrintTicket(ticket)}
                          className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                          title="הדפסה"
                          data-testid={`print-ticket-${ticket.id}`}
                        >
                          <Printer className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleOpenEdit(ticket)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          data-testid={`edit-ticket-${ticket.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(ticket.id)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                          data-testid={`delete-ticket-${ticket.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
              <Phone className="w-6 h-6 text-blue-600" />
              {editingTicket ? `עריכת קריאה #${editingTicket.callNumber}` : 'קריאת שירות חדשה'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Customer Name */}
            <div className="relative">
              <label className="text-sm font-semibold text-gray-700 mb-1 block">שם לקוח *</label>
              <Input
                value={form.customerName}
                onChange={(e) => handleCustomerInput(e.target.value)}
                onFocus={() => form.customerName && handleCustomerInput(form.customerName)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                placeholder="הקלד שם לקוח..."
                disabled={!!editingTicket}
                data-testid="ticket-customer-input"
              />
              {showSuggestions && (
                <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-xl border max-h-40 overflow-y-auto">
                  {customerSuggestions.map((name, idx) => (
                    <button
                      key={idx}
                      onMouseDown={() => selectCustomer(name)}
                      className="w-full text-right px-4 py-2 hover:bg-blue-50 text-sm border-b last:border-b-0"
                    >
                      {name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Date */}
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">תאריך</label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="ticket-date-input"
              />
            </div>

            {/* Issue Description */}
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">תיאור תקלה</label>
              <textarea
                value={form.issueDescription}
                onChange={(e) => setForm({ ...form, issueDescription: e.target.value })}
                placeholder="תאר את התקלה..."
                className="w-full min-h-[80px] p-3 border-2 border-gray-200 rounded-lg focus:border-blue-400 focus:outline-none resize-none text-sm"
                data-testid="ticket-issue-input"
              />
            </div>

            {/* Solution */}
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">פתרון תקלה</label>
              <textarea
                value={form.solution}
                onChange={(e) => setForm({ ...form, solution: e.target.value })}
                placeholder="תאר את הפתרון..."
                className="w-full min-h-[80px] p-3 border-2 border-gray-200 rounded-lg focus:border-green-400 focus:outline-none resize-none text-sm"
                data-testid="ticket-solution-input"
              />
            </div>

            {/* Status */}
            <div
              className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                form.status === 'closed'
                  ? 'border-green-400 bg-green-50'
                  : 'border-orange-300 bg-orange-50'
              }`}
              onClick={() => setForm({ ...form, status: form.status === 'closed' ? 'open' : 'closed' })}
              data-testid="ticket-status-toggle"
            >
              <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                form.status === 'closed'
                  ? 'bg-green-500 border-green-500 text-white'
                  : 'border-gray-300'
              }`}>
                {form.status === 'closed' && <CheckCircle className="w-4 h-4" />}
              </div>
              <span className={`text-sm font-semibold ${
                form.status === 'closed' ? 'text-green-700' : 'text-orange-700'
              }`}>
                {form.status === 'closed' ? 'נסגרה' : 'בטיפול'}
              </span>
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                onClick={handleSave}
                className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
                data-testid="save-ticket-btn"
              >
                {editingTicket ? 'עדכן' : 'צור קריאה'}
              </Button>
              <Button variant="outline" onClick={() => setShowDialog(false)} className="px-6">
                ביטול
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Customer Timeline Dialog */}
      <Dialog open={!!timelineCustomer} onOpenChange={() => setTimelineCustomer(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
              <History className="w-6 h-6 text-blue-600" />
              היסטוריית קריאות - {timelineCustomer}
            </DialogTitle>
          </DialogHeader>
          {timelineCustomer && (() => {
            const customerTickets = tickets
              .filter(t => t.customerName === timelineCustomer)
              .sort((a, b) => b.callNumber - a.callNumber);
            const openC = customerTickets.filter(t => t.status !== 'closed').length;
            const closedC = customerTickets.filter(t => t.status === 'closed').length;
            return (
              <div className="mt-2">
                {/* Customer Stats */}
                <div className="flex gap-3 mb-5">
                  <span className="text-sm bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg border border-blue-200 font-semibold">
                    סה"כ: {customerTickets.length}
                  </span>
                  <span className="text-sm bg-orange-50 text-orange-700 px-3 py-1.5 rounded-lg border border-orange-200 font-semibold">
                    בטיפול: {openC}
                  </span>
                  <span className="text-sm bg-green-50 text-green-700 px-3 py-1.5 rounded-lg border border-green-200 font-semibold">
                    נסגרו: {closedC}
                  </span>
                </div>

                {/* Timeline */}
                <div className="relative pr-6">
                  {/* Timeline line */}
                  <div className="absolute right-2 top-0 bottom-0 w-0.5 bg-gray-200" />

                  {customerTickets.map((t, idx) => (
                    <div key={t.id} className="relative mb-6 last:mb-0" data-testid={`timeline-item-${t.id}`}>
                      {/* Timeline dot */}
                      <div className={`absolute -right-[13px] top-1.5 w-4 h-4 rounded-full border-2 border-white shadow ${
                        t.status === 'closed' ? 'bg-green-500' : 'bg-orange-400'
                      }`} />

                      <div className={`bg-white rounded-lg border shadow-sm p-4 mr-4 ${
                        t.status === 'closed' ? 'border-green-200' : 'border-orange-200'
                      }`}>
                        {/* Header row */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                              <Hash className="w-3 h-3" />
                              {t.callNumber}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                              t.status === 'closed' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                            }`}>
                              {t.status === 'closed' ? 'נסגרה' : 'בטיפול'}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {t.date ? new Date(t.date + 'T00:00:00').toLocaleDateString('he-IL') : '-'}
                          </span>
                        </div>

                        {/* Issue */}
                        <p className={`text-sm mb-1 ${t.status === 'closed' ? 'text-gray-400' : 'text-gray-700'}`}>
                          <span className="font-semibold text-gray-800">תקלה: </span>
                          {t.issueDescription || '-'}
                        </p>

                        {/* Solution */}
                        {t.solution && (
                          <div className="bg-green-50 rounded p-2 border border-green-200 mt-2">
                            <p className="text-sm text-green-800">
                              <span className="font-semibold">פתרון: </span>{t.solution}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {customerTickets.length === 0 && (
                  <p className="text-center text-gray-500 py-8">אין קריאות עבור לקוח זה</p>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupportTickets;
